import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchResultsModal } from "./SearchResultsModal";
import { useSearch } from "./useSearch";
import { supabase } from "@/integrations/supabase/client";
import { SearchSuggestion } from "./types";
import { useTranslation } from "react-i18next";

export const GlobalSearch = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const searchRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { handleSearch, searchModalOpen, setSearchModalOpen, viewMode, setViewMode, searchQuery } = useSearch();
	const { t } = useTranslation();

	const handleKeyDown = (e: KeyboardEvent) => {
		// Open search on Cmd+K or Ctrl+K
		if ((e.metaKey || e.ctrlKey) && e.key === "k") {
			e.preventDefault();
			setIsOpen(prev => !prev);
		}

		// Close on escape
		if (e.key === "Escape" && !searchModalOpen) {
			setIsOpen(false);
		}
	};

	// Handle clicks outside the search component
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [searchModalOpen]);

	// Fetch suggestions as user types
	useEffect(() => {
		const fetchSuggestions = async () => {
			if (!query.trim()) {
				setSuggestions([]);
				setHighlightedIndex(-1);
				return;
			}
			
			setIsSearching(true);
			
			try {
				// Search candidates
				const { data: candidates } = await supabase
					.from("candidates")
					.select("id, name")
					.ilike("name", `%${query}%`)
					.limit(3);
					
				// Search jobs
				const { data: jobs } = await supabase
					.from("jobs")
					.select("id, title")
					.ilike("title", `%${query}%`)
					.limit(3);
					
				// Search companies
				const { data: companies } = await supabase
					.from("companies")
					.select("id, name")
					.ilike("name", `%${query}%`)
					.limit(3);
					
				const formattedSuggestions: SearchSuggestion[] = [
					...(candidates || []).map((candidate) => ({
						id: candidate.id,
						name: candidate.name,
						entityType: "candidate" as const,
					})),
					...(jobs || []).map((job) => ({
						id: job.id,
						name: job.title,
						entityType: "job" as const,
					})),
					...(companies || []).map((company) => ({
						id: company.id,
						name: company.name,
						entityType: "company" as const,
					})),
				];
				
				setSuggestions(formattedSuggestions);
				setHighlightedIndex(formattedSuggestions.length > 0 ? 0 : -1);
			} catch (error) {
				console.error("Error fetching search suggestions:", error);
			} finally {
				setIsSearching(false);
			}
		};
		
		const debounceTimer = setTimeout(() => {
			if (query.trim()) {
				fetchSuggestions();
			}
		}, 300);
		
		return () => clearTimeout(debounceTimer);
	}, [query]);

	// Keyboard navigation for suggestions
	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isOpen || suggestions.length === 0) return;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
		} else if (e.key === "Enter") {
			if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
				handleSuggestionClick(suggestions[highlightedIndex]);
			} else {
				onSubmit(e);
			}
		} else if (e.key === "Escape") {
			setIsOpen(false);
		}
	};

	// Handle suggestion click to navigate directly to the entity page
	const handleSuggestionClick = (suggestion: SearchSuggestion) => {
		setIsOpen(false);
		setQuery("");
		setHighlightedIndex(-1);
		// Navigate to the appropriate page based on entity type
		switch (suggestion.entityType) {
			case "candidate":
				navigate(`/candidates/${suggestion.id}`);
				break;
			case "job":
				navigate(`/jobs/${suggestion.id}`);
				break;
			case "company":
				navigate(`/companies/${suggestion.id}`);
				break;
			default:
				break;
		}
	};

	// Only open search results modal when Enter is pressed
	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (query.trim()) {
			handleSearch(query);
			setIsOpen(false);
			setHighlightedIndex(-1);
		}
	};

	return (
		<div className="relative w-full max-w-2xl mx-auto" ref={searchRef}>
			<div
				className={cn(
					"flex items-center rounded-full pr-2 w-full max-w-2xl mx-auto bg-white dark:bg-background border border-border",
					isOpen ? "ring-1 ring-primary" : ""
				)}
				onClick={() => setIsOpen(true)}
			>
				<Input
					type="search"
					placeholder={t('search.placeholder')}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 rounded-full text-lg px-6 py-3 min-w-[400px] max-w-2xl"
					onFocus={() => setIsOpen(true)}
					onKeyDown={handleInputKeyDown}
				/>
				<div className="flex items-center gap-1">
					{query && (
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 rounded-full"
							onClick={(e) => {
								e.stopPropagation();
								setQuery("");
							}}
						>
							<X size={14} />
						</Button>
					)}
					<div className="text-xs text-foreground/40 border border-foreground/20 rounded px-1.5 py-0.5">
						⌘K
					</div>
				</div>
			</div>

			{/* Suggestions dropdown */}
			{isOpen && query.trim() && (
				<div className="absolute left-0 right-0 mt-2 z-50">
					<div className="rounded-xl shadow-lg p-2 max-h-60 overflow-y-auto bg-white dark:bg-background border border-border">
						{isSearching && (
							<p className="text-sm text-muted-foreground px-2 py-2">{t('search.searching')}</p>
						)}
						{!isSearching && suggestions.length > 0 && (
							<ul className="space-y-1">
								{suggestions.map((suggestion, idx) => (
									<li key={`${suggestion.entityType}-${suggestion.id}`}>
										<button
											type="button"
											className={cn(
												"w-full text-left px-3 py-2 hover:bg-accent rounded-md flex items-center gap-2 transition-colors",
												highlightedIndex === idx ? "bg-accent" : ""
											)}
											onMouseEnter={() => setHighlightedIndex(idx)}
											onClick={() => handleSuggestionClick(suggestion)}
										>
											{suggestion.entityType === "candidate" && <Search size={14} />}
											{suggestion.entityType === "job" && <Search size={14} />}
											{suggestion.entityType === "company" && <Search size={14} />}
											<span>{suggestion.name}</span>
											<span className="text-xs text-muted-foreground ml-auto">
												{t(`search.entityTypes.${suggestion.entityType}`)}
											</span>
										</button>
									</li>
								))}
							</ul>
						)}
						{!isSearching && suggestions.length === 0 && (
							<p className="text-sm text-muted-foreground px-2 py-2">{t('search.noResults')}</p>
						)}
					</div>
				</div>
			)}

			{/* Search results modal (unchanged) */}
			<SearchResultsModal
				isOpen={searchModalOpen}
				onOpenChange={setSearchModalOpen}
				searchQuery={searchQuery}
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			/>
		</div>
	);
};
